<template>
  <available-section section="reports">
    <main class="report-employees-workload">
      <div class="report-employees-workload__header">
        <h1 class="report-employees-workload__title">Загруженность сотрудников</h1>
        <form class="report-employees-workload__form">
          <div class="report-employees-workload__row">
            <date-picker-dialog
              range
              v-bind:value="period"
              v-on:input="onInputPeriod"
              v-on:close="onCloseDatePickerDialog"
            >
              <template v-slot:trigger="{ listenerObject }">
                <button
                  type="button"
                  v-on="listenerObject"
                  class="report-employees-workload__search"
                >
                  <div v-if="period.length >= 1">{{periodTitle}}</div>
                  <div v-else>Выберите период</div>
                </button>
              </template>
            </date-picker-dialog>
          </div>
          <div class="report-employees-workload__tags">
            <button
              type="button"
              v-on:click="setPeriodByYesterday"
              class="report-employees-workload__tag"
              v-bind:class="{ active: periodName === $options.periodYesterday }"
            >Вчера</button>
            <button
              type="button"
              v-on:click="setPeriodByWeek"
              class="report-employees-workload__tag"
              v-bind:class="{ active: periodName === $options.periodWeek }"
            >Неделя</button>
            <button
              type="button"
              v-on:click="setPeriodByMonth"
              class="report-employees-workload__tag"
              v-bind:class="{ active: periodName === $options.periodMonth }"
            >Mесяц</button>
            <button
              type="button"
              v-on:click="setPeriodByYear"
              class="report-employees-workload__tag"
              v-bind:class="{ active: periodName === $options.periodYear }"
            >Год</button>
          </div>
          <div>
            <multiple-autocomplete-input
              chips
              item-text="name"
              item-value="id"
              label="Выберите сотрудников"
              multiple
              removal-per-chip
              select-all-text="Все сотрудники"
              set-select-all-option
              small-chips
              v-bind:items="employeeFilter"
              v-model="selectedEmployees"
              class="report-employees-workload__autocomplete"
            />
          </div>
        </form>
      </div>
      <div class="report-employees-workload__body">
        <bar-diagram
          v-bind:items="sortedEmployeesForDiagram"
          title="Диаграмма"
          item-label-key="surnameWithInitials"
          item-value-key="workloadPercent"
        />
        <div class="report-employees-workload__table">
          <div class="report-employees-workload__table__head">
            <div>ФИО</div>
            <div>Специализация</div>
            <input-sort
              v-bind:class="{ selected: !!sortByWorkHours }"
              v-model="sortByWorkHours"
              v-on:input="sortEmployees('sortByWorkHours', $event)"
              name="total-price"
              desc-first
              sort-class="sort"
            >Рабочее время (Часы из графика)</input-sort>
            <input-sort
              v-bind:class="{ selected: !!sortByWorkloadHours }"
              v-model="sortByWorkloadHours"
              v-on:input="sortEmployees('sortByWorkloadHours', $event)"
              name="total-price"
              desc-first
              sort-class="sort"
            >Занято (часы из расписания)</input-sort>
            <input-sort
              v-bind:class="{ selected: !!sortByWorkloadPercent }"
              v-model="sortByWorkloadPercent"
              v-on:input="sortEmployees('sortByWorkloadPercent', $event)"
              name="total-price"
              desc-first
              sort-class="sort"
            >Занято (в %)</input-sort>
          </div>
          <div
            v-for="(employee, key) in employeeList"
            v-bind:key="key"
            class="report-employees-workload__table__row"
          >
            <div data-label="ФИО">{{employee.name}}</div>
            <div data-label="ФИО">{{employee.specialtyName}}</div>
            <div data-label="Рабочее время (Часы из графика)">{{employee.workHours}}</div>
            <div data-label="Занято (часы из расписания)">{{employee.workloadHours}}</div>
            <div data-label="Занято (в %)">{{employee.workloadPercent}} %</div>
          </div>
        </div>
      </div>
    </main>
  </available-section>
</template>

<script>
import DatePickerDialog from '@/components/DatePickerDialog.vue';
import notifierMixin from '@/mixins/notifierMixin';
import AvailableSection from '@/views/ClinicPanel/AvailableSection.vue';
import MultipleAutocompleteInput from '@/components/MultipleAutocompleteInput.vue';
import debounce from 'lodash.debounce';
import InputSort from '@/components/InputSort.vue';
import BarDiagram from '@/components/BarDiagram.vue';
import reportSalesMixin from '@/mixins/reportSalesMixin';

export default {
  components: {
    BarDiagram,
    InputSort,
    MultipleAutocompleteInput,
    AvailableSection,
    DatePickerDialog,
  },
  computed: {
    /**
     * @description Отсортированные сотрудники для диаграммы
     * @returns {[]}
     */
    sortedEmployeesForDiagram() {
      const list = [...this.employeeList];
      return list.sort((line1, line2) => {
        if (line2.workloadPercent < line1.workloadPercent) {
          return -1;
        }
        if (line2.workloadPercent > line1.workloadPercent) {
          return 1;
        }
        return 0;
      });
    },
    /**
     * @description Данные запроса
     * @returns {Object}
     */
    viewQuery() {
      return {
        period: this.period,
        selectedEmployees: this.selectedEmployees,
      };
    },
  },
  created() {
    this.setPeriodByMonth();
    this.fetchFilter().then(() => {
      this.selectedEmployees = this.employeeFilter.map((employee) => employee.id);
    });
  },
  data() {
    return {
      employeeFilter: [],
      selectedEmployees: [],
      period: [],
      employeeList: [],
      sortByWorkHours: '',
      sortByWorkloadHours: '',
      sortByWorkloadPercent: 'desc',
    };
  },
  methods: {
    /**
     * @description Очистить другие сортировки
     * @param notClearableType
     */
    clearSorts(notClearableType = '') {
      this.sortByWorkHours = notClearableType === 'sortByWorkHours' ? this.sortByWorkHours : '';
      this.sortByWorkloadHours = notClearableType === 'sortByWorkloadHours' ? this.sortByWorkloadHours : '';
      this.sortByWorkloadPercent = notClearableType === 'sortByWorkloadPercent' ? this.sortByWorkloadPercent : '';
    },
    /**
     * @description Сортировать сотрудников
     * @param type
     * @param direction
     */
    sortEmployees(type, direction) {
      this.clearSorts(type);
      this.requestReport(type, direction);
    },
    /**
     * @description Затянуть фильтр.
     * @returns {Promise}
     */
    fetchFilter() {
      const onSuccess = (filters) => {
        this.employeeFilter = filters.employees;
      };
      const onFail = (error) => {
        this.notifyFail(error.title);
      };
      const response = this.$reportGateway.getFilterForReportEmployeesWorkload();
      response.then((either) => either.fold(onFail, onSuccess));
      return response;
    },
    /**
     * @description Создать сортировку.
     * @param {string} type       Тип сортировки.
     * @param {string} direction  Направление.
     * @returns {{type: string, direction: string}}
     */
    makeSort(type = '', direction = '') {
      if (type === '' && direction === '') {
        return {
          type: 'sortByWorkloadPercent',
          direction: 'desc',
        };
      }
      if (type !== '' || direction !== '') {
        this.clearSorts(type);
      }
      return {
        type,
        direction,
      };
    },
    /**
     * @description Запросить отчет
     */
    requestReport(type = '', direction = '') {
      const onSuccess = (employees) => {
        this.employeeList = employees;
      };
      const onFail = (data) => {
        this.notifyFail(data.title);
      };
      const period = this.period.map((date) => date.toISOString().split('T')[0]);
      if (this.period.length < 2 || this.selectedEmployees.length === 0) {
        return;
      }
      const sort = this.makeSort(type, direction);
      this.$reportGateway.getReportByEmployeesWorkload(
        period,
        this.selectedEmployees,
        sort,
      ).then((either) => {
        either.fold(onFail, onSuccess);
      });
    },
  },
  watch: {
    viewQuery: debounce(function _() {
      this.requestReport();
    }, 300),
  },
  mixins: [notifierMixin, reportSalesMixin],
  name: 'ClinicPanelReportEmployeesWorkload',
};
</script>

<style lang="scss">
@use "@/scss/buttons";
.menuable__content__active {
  min-width: 250px !important;
}
.report-employees-workload {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  &__header {
    background: #F2F5F8;
    width: 100%;
    padding: 38px 20px 48px 23px;
  }
  &__form {
    display: flex;
    flex-flow: row wrap;
    align-items: flex-end;
    gap: 23px;
    margin: 30px 0 0 0;
  }
  &__row {
    display: flex;
    flex-direction: row;
  }
  &__search {
    border: 1px solid #C2CFE0;
    border-radius: 4px;
    background: #FFFFFF;
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;
    height: 40px;
    min-width: 250px;
    width: 100%;
    padding: 0 0 0 10px;
    display: flex;
    align-items: center;
    position: relative;
    &:after {
      content: '';
      background-color: #EAEFF3;
      border-radius: 0 4px 4px 0;
      width: 38px;
      height: 38px;
      position: absolute;
      right: 0;
      overflow: hidden;
      background-image: url("@/assets/img/calendar-grey.svg");
      background-position: center;
    }
    &__exit {
      width: 40px;
      height: 40px;
      border: 1px solid #C2CFE0;
      background-color: #EAEFF3;
      border-radius: 4px;
      background-image: url("@/assets/img/exit-dark-grey.svg");
      background-position: center;
      margin: 0 0 0 -2px;
    }
  }
  &__tags {
    display: flex;
    flex-direction: row;
    align-items: center;
    border-radius: 4px;
    overflow: hidden;
  }
  &__tag {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    background: #EAEFF3;
    font-weight: 400;
    font-size: 14px;
    line-height: 17px;
    color: #192A3E;
    padding: 0 13px;
    &:hover,
    &.active {
      background: #0489E0;
      color: #FFFFFF;
    }
  }
  &__autocomplete {
    min-width: 300px !important;
    max-width: fit-content;
    &.v-input .v-label {
      height: 32px;
      line-height: 32px;
    }
    &.v-text-field .v-label {
      top: 7px;
    }
    label.v-label.v-label--active.theme--light {
      top: 0;
    }
    &.v-text-field .v-input__append-inner, .v-text-field .v-input__prepend-inner {
      margin-top: 12px;
    }
  }
  &__button {
    @include buttons.btn-blue(250px, 40px, 18px);
  }
  &__select {
    max-width: 570px;
    width: 100%;
  }
  &__body {
    padding: 48px 20px 150px 23px;
  }
  &__table {
    margin: 114px 0 0 0;
    &__head,
    &__row {
      width: 100%;
      text-align: left;
      cursor: pointer;
      display: grid;
      grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
      align-items: center;
      font-size: 14px;
      line-height: 17px;
    }
    &__head {
      width: 100%;
      font-weight: 700;
      border-bottom: 2px solid #0489e0;
      padding: 10px 0;
      div {
        padding: 0 8px;
        cursor: initial;
      }
      .sort {
        text-align: left;
        display: flex;
        align-items: center;
        &:after {
          content: '';
          background-image: url("@/assets/img/checked/checked-grey.svg");
          background-repeat: no-repeat;
          background-size: contain;
          background-position: center;
          width: 10px;
          min-width: 10px;
          height: 10px;
          min-height: 10px;
          margin: 0 0 0 5px;
        }
        &:hover {
          text-decoration: none;
        }
      }
    }
    &__row {
      border-bottom: 1px solid #eaeff3;
      padding: 12px 0;
      cursor: initial;
      div {
        padding: 0 8px;
      }
      &:hover {
        background: #eaeff3;
      }
    }
  }
}
.selected {
  color: var(--action-color);
}
@media screen and (max-width: 1500px) {
  .report-employees-workload {
    &__table {
      &__head,
      &__row {
        grid-template-columns: 400px repeat(4, 1fr);
      }
    }
  }
}
@media screen and (max-width: 1040px) {
  .report-employees-workload {
    &__table {
      &__head {
        display: none;
      }
      &__row {
        grid-template-columns: 1fr;
        grid-gap: 5px;
        margin: 0 0 20px 0;
        div {
          display: flex;
          flex-direction: column;
          margin: 0 0 10px 0;
        }
        div:before {
          content: attr(data-label);
          font-weight: 700;
          margin-right: 20px;
        }
      }
    }
  }
}
@media screen and (max-width: 600px) {
  .report-employees-workload {
    &__form {
      flex-direction: column;
      align-items: flex-start;
    }
    &__select {
      width: 100%;
    }
    &__button {
      width: 100%;
    }
    &__table {
      margin: 40px 0 0 0;
    }
  }
}
@media screen and (max-width: 480px) {
  .report-employees-workload {
    &__header {
      padding: 40px 20px;
    }
    &__body {
      padding: 40px 20px 100px 20px;
    }
    .diagram {
      display: none;
    }
    &__table {
      margin-top: 0;
    }
  }
}

</style>
